import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useCartStore, lineUnitWithModifiers } from '../stores/cart.store.js'
import { useSessionStore } from '../stores/session.store.js'
import {
  createOrder, addOrderItem, applyLoyalty, fetchPaymentMethods, getMyTableOrder, setOrderTip, submitRound,
  type PaymentMethod, type PaymentMethodsGate,
} from '../lib/api.js'
import { formatMoney } from '../lib/money.js'
import { NintaiGOHeader } from '../components/NintaiGOHeader.js'
import { GuestTipSelector } from '../components/GuestTipSelector.js'
import { useActiveOrderStore } from '../stores/active-order.store.js'
import { useStadiumMode } from '../lib/stadium.js'
import { StadiumCheckout } from './StadiumCheckout.js'

/** Etiqueta legible por método (enum canónico del backend). */
const METHOD_LABEL: Record<PaymentMethod, string> = {
  CASH: 'Efectivo',
  TERMINAL: 'Tarjeta (terminal)',
  ONLINE: 'Pago en línea',
}
const METHOD_ORDER: PaymentMethod[] = ['CASH', 'TERMINAL', 'ONLINE']

/** Deriva la lista de métodos HABILITADOS del gate (orden estable). */
function enabledMethods(gate: PaymentMethodsGate | null): PaymentMethod[] {
  if (!gate) return []
  return METHOD_ORDER.filter((m) =>
    (m === 'CASH' && gate.cashEnabled) ||
    (m === 'TERMINAL' && gate.terminalEnabled) ||
    (m === 'ONLINE' && gate.onlineEnabled))
}

/**
 * Métodos que se le OFRECEN al comensal al ordenar, según el destino:
 *   - DINE_IN: sólo efectivo/terminal (preferencia suave). El pago en línea NO va
 *     aquí — en dine-in se paga al CERRAR la cuenta (flujo aparte), no al ordenar.
 *   - TAKEOUT: los habilitados, con "Pago en línea" PRIMERO (prepago = disparo a
 *     cocina tras cobro); efectivo/terminal = pagar al recoger en caja.
 */
function methodsForDestino(gate: PaymentMethodsGate | null, isTakeout: boolean): PaymentMethod[] {
  const base = enabledMethods(gate)
  if (!isTakeout) return base.filter((m) => m !== 'ONLINE')
  return [...base.filter((m) => m === 'ONLINE'), ...base.filter((m) => m !== 'ONLINE')]
}

/** Modo estadio + QR para llevar → resumen y pago de demostración. Cualquier
 *  otro caso usa el carrito de siempre, sin cambios. */
export function Cart() {
  const stadium = useStadiumMode()
  const orderMode = useSessionStore((s) => s.session?.orderMode)
  if (stadium && orderMode === 'TAKEOUT') return <StadiumCheckout />
  return <DefaultCart />
}

function DefaultCart() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const session = useSessionStore((s) => s.session)
  /** OhSir: takeout = el pedido pasa por PAGO antes de cocina (no se dispara). */
  const isTakeout = session?.orderMode === 'TAKEOUT'
  const lines = useCartStore((s) => s.lines)
  const subtotal = useCartStore((s) => s.subtotalCents())
  const setQuantity = useCartStore((s) => s.setQuantity)
  const remove = useCartStore((s) => s.remove)
  const clear = useCartStore((s) => s.clear)
  const [submitting, setSubmitting] = useState(false)
  const [progress, setProgress] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** Gate de métodos del negocio: undefined = cargando, null = no configurado. */
  const [methodsGate, setMethodsGate] = useState<PaymentMethodsGate | null | undefined>(undefined)
  const [method, setMethod] = useState<PaymentMethod | null>(null)
  /** Propina elegida (pago en mostrador). Sólo se ofrece si el negocio configuró
   *  sus presets (gate.tipPresets) — opt-in por tenant, cero cambio para el resto. */
  const [tipCents, setTipCents] = useState(0)
  const addActiveOrder = useActiveOrderStore((s) => s.add)

  const token = session?.token
  useEffect(() => {
    if (!token) return
    let alive = true
    void (async () => {
      const r = await fetchPaymentMethods(token)
      if (!alive) return
      const gate = r.ok ? r.data.methods : null
      setMethodsGate(gate)
      // Si sólo hay un método ofrecido (según destino), pre-selecciónalo (menos fricción).
      const avail = methodsForDestino(gate, isTakeout)
      if (avail.length === 1) setMethod(avail[0]!)
    })()
    return () => { alive = false }
  }, [token, isTakeout])

  if (!session) {
    navigate(`/r/${slug}`, { replace: true })
    return <></>
  }

  const available = methodsForDestino(methodsGate ?? null, isTakeout)
  const methodsLoading = methodsGate === undefined
  const noMethods = !methodsLoading && available.length === 0
  const singleMethod = available.length === 1
  const onlineSelected = method === 'ONLINE'
  // Propina en pago en MOSTRADOR (takeout + efectivo/terminal): sólo si el
  // negocio configuró presets. El prepago online conserva su paso de propina
  // propio (PaymentOnline) — aquí no se duplica.
  const tipPresets = methodsGate?.tipPresets ?? []
  const offerCounterTip = isTakeout && method !== null && method !== 'ONLINE' && tipPresets.length > 0
  const effectiveTip = offerCounterTip ? tipCents : 0

  // Nonce de idempotencia por checkout: estable a través de reintentos (mismo
  // pedido → misma orden), se renueva al enviar uno nuevo con éxito.
  const checkoutNonce = useRef<string | null>(null)

  async function submit(): Promise<void> {
    if (!session) return
    if (lines.length === 0) return
    // Guard de re-entrada (doble-tap): si ya hay un envío en curso, ignora.
    if (submitting) return
    // Genera el nonce una vez por intento de checkout; en un reintento tras fallo
    // se reusa → el backend deduplica la orden (no crea dos).
    if (checkoutNonce.current === null) checkoutNonce.current = crypto.randomUUID()
    // El método de pago es OBLIGATORIO: el staff no puede aceptar una orden sin
    // método elegido (gate de aceptación). No dejamos enviar sin elegir.
    if (!method) {
      setError('Elige un método de pago antes de enviar tu pedido.')
      return
    }
    setSubmitting(true)
    setError(null)
    setProgress('Creando orden…')

    // 1) ¿Orden nueva o ronda sobre la cuenta que ya existe?
    //
    // MESA = cuenta abierta. El comensal está físicamente ahí y pide rondas: TODO
    // debe ir a la MISMA cuenta. Crear una segunda orden sobre la misma sesión la
    // partía en dos, y como `/orders/by-session` resuelve con findFirst(openedAt
    // DESC) sólo devolvía la última → la ronda anterior quedaba ABIERTA Y SIN
    // COBRAR al pagar la cuenta. Fuga de dinero, no un detalle de UX.
    //
    // PARA LLEVAR = pedido cerrado al enviarse. Cada envío es independiente: su
    // propio código de recolección y su propio cobro. Ahí sí se crea orden nueva.
    let orderId: string | null = null
    let pickupCode: string | null = null
    // ¿Estos ítems son una RONDA NUEVA sobre una cuenta ya disparada? Sólo si
    // reusamos una orden que YA salió a cocina (OPEN/SENT_TO_KITCHEN). Si la
    // orden sigue en PENDING_ACCEPTANCE, es la ronda 1 todavía sin aceptar —
    // esa la autoriza el staff a nivel ORDEN, no se somete ronda aparte.
    let needsRoundSubmit = false
    if (!isTakeout) {
      const open = await getMyTableOrder(session.token)
      if (open.ok && open.data.status !== 'CLOSED' && open.data.status !== 'CANCELLED') {
        orderId = open.data.id
        needsRoundSubmit = open.data.status === 'OPEN' || open.data.status === 'SENT_TO_KITCHEN'
        setProgress('Sumando a tu cuenta…')
      }
    }

    if (!orderId) {
      // Orden nueva (sin items — los agregamos uno a uno via /items).
      // chosenPaymentMethod queda en la orden para que el staff valide y acepte.
      const created = await createOrder(session.token, {
        tenantId: session.tenantId,
        tableSessionId: session.tableSessionId,
        type: isTakeout ? 'TAKEOUT' : 'DINE_IN',
        chosenPaymentMethod: method,
        idempotencyKey: checkoutNonce.current,
      })
      if (!created.ok) {
        setSubmitting(false)
        setProgress(null)
        setError(`No se pudo crear la orden: ${created.error.code} — ${created.error.message}`)
        return
      }
      orderId = created.data.id
      pickupCode = created.data.pickupCode ?? null
    }

    // 2) Agregar cada línea como item — modifiers incluidos (la comanda KDS los lleva).
    //    unitPriceCents = precio BASE; el backend suma los deltas de modifiers.
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i]!
      setProgress(`Agregando ${i + 1}/${lines.length}…`)
      const itemBody: {
        productId: string
        name: string
        quantity: number
        unitPriceCents: number
        modifiers?: { modifierId: string; name: string; priceDeltaCents: number }[]
        notes?: string
      } = {
        productId: l.productId,
        name: l.productName,
        quantity: l.quantity,
        unitPriceCents: l.unitPriceCents,
      }
      if (l.modifiers.length > 0) itemBody.modifiers = l.modifiers
      if (l.notes) itemBody.notes = l.notes
      const r = await addOrderItem(session.token, orderId, itemBody)
      if (!r.ok) {
        setSubmitting(false)
        setProgress(null)
        setError(`Error agregando ${l.productName}: ${r.error.code} — ${r.error.message}`)
        return
      }
    }

    // Orden creada + items agregados con éxito → el nonce cumplió su vida; el
    // próximo pedido genera uno nuevo. (Un reintento tras fallo, en cambio,
    // conserva el nonce y deduplica la orden.)
    checkoutNonce.current = null

    // ADR-0013 F1: con la orden ya armada, disparamos el descuento de lealtad
    // (server-authoritative: el backend calcula el monto desde el nivel del
    // comensal). Best-effort — un fallo NO debe bloquear el pago; el gateway
    // leerá el total ya descontado si aplicó.
    await applyLoyalty(session.token, orderId).catch(() => undefined)

    // Propina en pago en mostrador: viaja EN la orden (tipCents) y el cajero la
    // cobra junto con el consumo. Un reintento; si aun así falla, se AVISA
    // (nunca silencio en dinero) y el pedido sigue — la propina se puede indicar
    // en caja, no vale bloquear la comida por ella.
    if (isTakeout && method !== 'ONLINE' && effectiveTip > 0) {
      setProgress('Registrando propina…')
      let tipRes = await setOrderTip(session.token, orderId, effectiveTip)
      if (!tipRes.ok) tipRes = await setOrderTip(session.token, orderId, effectiveTip)
      if (!tipRes.ok) {
        window.alert('No pudimos registrar tu propina en el pedido. Al pagar en caja, indícasela al cajero. ¡Gracias!')
      }
    }

    // Rastro del pedido activo (takeout): lo lee el Menú para pintar el banner
    // "pedido en curso" — sin esto, back desde el tracker = menú vacío. Es una
    // LISTA: cada pedido takeout es independiente (su propio código), así que si
    // el comensal hace varios los vemos TODOS juntos.
    if (isTakeout) {
      addActiveOrder({ orderId, tableSessionId: session.tableSessionId, pickupCode, placedAt: Date.now() })
    }

    // 3a) TAKEOUT + PAGO EN LÍNEA (prepago): el comensal pasa a la captura de
    //     tarjeta. Cocina se dispara sólo cuando se confirma el cobro (anti-
    //     chargeback: cobrar antes de servir).
    if (isTakeout && method === 'ONLINE') {
      clear()
      navigate(`/r/${slug}/pago/${orderId}`, { replace: true })
      return
    }

    // 3b) Resto — DINE_IN (paga al cerrar con el mesero) o TAKEOUT efectivo/
    //     terminal (paga al recoger en caja): la orden nace PENDING_ACCEPTANCE y
    //     espera a que el STAFF la acepte desde /floor (el accept la abre y la
    //     comanda). El comensal pasa al seguimiento.

    // RONDA NUEVA sobre una cuenta ya disparada: agrupamos lo recién agregado en
    // una ronda que el mesero acepta. Sin esto, lo nuevo quedaba suelto en la
    // orden y no llegaba a cocina. Best-effort con un reintento — un fallo no
    // debe perder el pedido; el staff igual lo verá al recuperar la mesa.
    if (needsRoundSubmit) {
      setProgress('Enviando tu ronda…')
      let sr = await submitRound(session.token, orderId)
      if (!sr.ok) sr = await submitRound(session.token, orderId)
      if (!sr.ok) {
        window.alert('Agregamos tus productos a la cuenta, pero avísale al mesero para que confirme esta ronda.')
      }
    }

    clear()
    navigate(`/r/${slug}/order/${orderId}`, { replace: true })
  }

  return (
    <div className="app">
      <NintaiGOHeader slug={slug} tableCode={session.tableCode} customerName={session.customerName} branchId={session.branchId} />
      <div className="container">
        <button
          className="btn btn-ghost"
          style={{ width: 'auto', marginBottom: 12 }}
          onClick={() => navigate(`/r/${slug}/menu`)}
        >
          ← Seguir ordenando
        </button>
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Tu pedido</h2>
          {lines.length === 0 && <div className="muted">Carrito vacío.</div>}
          {lines.map((l) => (
            <div
              key={l.lineId}
              className="row"
              style={{ alignItems: 'flex-start', borderTop: '1px solid var(--line)', paddingTop: 10, marginTop: 10 }}
            >
              <div className="grow">
                <div style={{ fontWeight: 600 }}>{l.productName}</div>
                {l.modifiers.length > 0 && (
                  <div className="muted" style={{ marginTop: 2 }}>
                    {l.modifiers
                      .map((m) => (m.priceDeltaCents !== 0
                        ? `${m.name} (+${formatMoney(m.priceDeltaCents)})`
                        : m.name))
                      .join(' · ')}
                  </div>
                )}
                {l.notes && <div className="muted">{l.notes}</div>}
                <div className="muted">{formatMoney(lineUnitWithModifiers(l) * l.quantity)}</div>
              </div>
              <div className="qty">
                <button onClick={() => setQuantity(l.lineId, l.quantity - 1)}>−</button>
                <span>{l.quantity}</span>
                <button onClick={() => setQuantity(l.lineId, l.quantity + 1)}>+</button>
              </div>
              <button
                className="btn btn-ghost"
                style={{ width: 'auto', padding: '6px 10px', border: 'none' }}
                onClick={() => remove(l.lineId)}
              >
                Quitar
              </button>
            </div>
          ))}
        </div>
        {error && (
          <div className="card" style={{ borderColor: 'var(--danger)' }}>
            <strong className="danger">No se pudo enviar a cocina</strong>
            <div style={{ marginTop: 6, fontSize: 13, color: 'var(--danger)', wordBreak: 'break-word' }}>{error}</div>
          </div>
        )}
        {lines.length > 0 && (
          <div className="card">
            <h3 style={{ marginTop: 0, marginBottom: 4 }}>
              {isTakeout ? '¿Cuál es tu método de pago?' : '¿Cómo prefieres pagar?'}
            </h3>
            <p className="muted" style={{ marginTop: 0, marginBottom: 12 }}>
              {isTakeout
                ? 'Confirmamos tu pedido con este método.'
                : 'Es tu preferencia; al cerrar la cuenta pagas con el mesero.'}
            </p>
            {methodsLoading && <div className="muted">Cargando métodos…</div>}
            {noMethods && (
              <div className="danger" style={{ fontSize: 13 }}>
                El negocio aún no habilita métodos de pago. Pide ayuda al personal.
              </div>
            )}
            {/* Un solo método → se muestra como info, no como elección. */}
            {!methodsLoading && singleMethod && (
              <div className="muted">
                Método de pago: <strong>{METHOD_LABEL[available[0]!]}</strong>
              </div>
            )}
            {!methodsLoading && available.length > 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {available.map((m) => {
                  const selected = method === m
                  return (
                    <button
                      key={m}
                      type="button"
                      className={`btn ${selected ? 'btn-primary' : 'btn-ghost'}`}
                      aria-pressed={selected}
                      onClick={() => setMethod(m)}
                      style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                    >
                      {selected ? '● ' : '○ '}{METHOD_LABEL[m]}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}
        {/* Propina (pago en mostrador) — opt-in por negocio via gate.tipPresets. */}
        {lines.length > 0 && offerCounterTip && (
          <div className="card">
            <h3 style={{ marginTop: 0, marginBottom: 4 }}>¿Deseas dejar propina?</h3>
            <p className="muted" style={{ marginTop: 0, marginBottom: 12 }}>
              Es opcional; se suma a tu ticket y la pagas en caja.
            </p>
            <GuestTipSelector baseCents={subtotal} valueCents={tipCents} onChange={setTipCents} presets={tipPresets} />
          </div>
        )}
        <div className="card">
          {effectiveTip > 0 && (
            <>
              <div className="row">
                <div className="grow">Consumo</div>
                <div>{formatMoney(subtotal)}</div>
              </div>
              <div className="row">
                <div className="grow">Propina</div>
                <div>{formatMoney(effectiveTip)}</div>
              </div>
            </>
          )}
          <div className="row">
            <div className="grow"><strong>Total</strong></div>
            <div><strong>{formatMoney(subtotal + effectiveTip)}</strong></div>
          </div>
        </div>
        <button
          className="btn btn-primary"
          onClick={submit}
          disabled={submitting || lines.length === 0 || methodsLoading || noMethods || !method}
          style={{ marginTop: 12 }}
        >
          {submitting ? (
            <>
              <span className="spinner" /> {progress ?? 'Enviando…'}
            </>
          ) : isTakeout && onlineSelected ? (
            'Pagar y enviar'
          ) : (
            'Enviar a cocina'
          )}
        </button>
        <p className="muted" style={{ textAlign: 'center', marginTop: 12 }}>
          {isTakeout && onlineSelected
            ? 'Preparamos tu pedido en cuanto se confirme el pago.'
            : isTakeout
              ? 'Pagas al recoger en caja.'
              : 'Pagas al cerrar tu cuenta con el mesero.'}
        </p>
      </div>
      {/* Acceso persistente a la cuenta abierta (sólo mesa); en el carrito no
          ofrecemos "pedir cuenta" (aún estás armando el pedido). */}
    </div>
  )
}
